import { LogRecord } from "@fairfleet/geotab";
import { LineSeriesType } from "@mui/x-charts-pro";
import { LineChartPro } from "@mui/x-charts-pro/LineChartPro";
import { round } from "lodash";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { interpolateAccidentStatusData } from "./interpolateAccidentStatusData";
import { QueryStatusData } from "@/models/QueryDiagnosticDeviceData";

interface Props {
  forwardBreakingGForces: QueryStatusData[];
  sideTosideGForces: QueryStatusData[];
  logRecords: LogRecord[];
}

const GForcesDetailChart: React.FC<Props> = ({
  forwardBreakingGForces,
  sideTosideGForces,
  logRecords,
}) => {
  const uniqueDates = new Set<string>();

  forwardBreakingGForces.forEach(d => uniqueDates.add(d.dateTime.toISOString()));
  sideTosideGForces.forEach(d => uniqueDates.add(d.dateTime.toISOString()));
  logRecords.forEach(d => uniqueDates.add(d.dateTime.toISOString()));

  const sortedDates = Array.from(uniqueDates)
    .map(d => new Date(d))
    .sort((a, b) => a.getTime() - b.getTime());

  const dataset = sortedDates.map(date => {
    const dateString = date.toISOString();
    return {
      dateTime: date,
      forwardBreakingGForces:
        forwardBreakingGForces.find(d => d.dateTime.toISOString() === dateString)?.data ?? null,
      sideTosideGForces:
        sideTosideGForces.find(d => d.dateTime.toISOString() === dateString)?.data ?? null,
      speed: logRecords.find(d => d.dateTime.toISOString() === dateString)?.speed ?? null,
    };
  });

  const interpolatedData = useMemo(() => interpolateAccidentStatusData(dataset), [dataset]);
  const { t, i18n } = useTranslation();

  const series = useMemo(() => {
    return [
      {
        dataKey: "forwardBreakingGForces",
        label: t("faultOverview.forwardBreakingGForces"),
        color: "#ffb476",
        yAxisId: "g-forces",
        connectNulls: true,
        showMark: false,
        curve: "linear",
        valueFormatter: (value: number) => (value !== null ? `${round(value, 2)} G` : ""),
      },
      {
        dataKey: "sideTosideGForces",
        label: t("faultOverview.sideTosideGForces"),
        color: "#5fc366",
        yAxisId: "g-forces",
        connectNulls: true,
        showMark: false,
        curve: "linear",
        valueFormatter: (value: number) => (value !== null ? `${round(value, 2)} G` : ""),
      },
      {
        dataKey: "speed",
        label: t("faultOverview.speed"),
        color: "#0078d3",
        yAxisKey: "speed",
        showMark: false,
        connectNulls: true,
        curve: "linear",
        valueFormatter: (value: number) => (value !== null ? `${round(value, 2)} km/h` : ""),
      },
    ] as unknown as LineSeriesType[];
  }, [t]);

  return (
    <LineChartPro
      experimentalMarkRendering={true}
      xAxis={[
        {
          scaleType: "time",
          dataKey: "dateTime",
          tickNumber: 5,
          zoom: true,
          valueFormatter: (value: number, context) =>
            context.location === "tick"
              ? new Date(value).toLocaleTimeString(i18n.language, {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: false,
                })
              : new Date(value).toLocaleString(i18n.language, {
                  hour: "2-digit",
                  minute: "2-digit",
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  second: "2-digit",
                  hour12: false,
                }),
        },
      ]}
      tooltip={{ trigger: "axis" }}
      series={series}
      dataset={interpolatedData}
      leftAxis={"g-forces"}
      rightAxis={"speed"}
      grid={{ vertical: false, horizontal: true }}
      yAxis={[
        { id: "speed", min: 0, valueFormatter: value => `${value} km/h`, zoom: true },
        { id: "g-forces", valueFormatter: value => `${value} G`, zoom: true },
      ]}
      margin={{ right: 100 }}
      slotProps={{
        legend: {
          position: {
            vertical: "top",
            horizontal: "middle",
          },
          itemMarkWidth: 20,
          itemMarkHeight: 2,
          markGap: 5,
          itemGap: 10,
        },
      }}
    />
  );
};

export default GForcesDetailChart;
